import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, computed } from "vue";
import { usePlugins } from "@/stores/helpers/usePlugins";
import client from "@/api/openapi-ts";
import type { components } from "@/api/schema.d.ts";
import { useErrorStore } from "@/stores/ErrorStore";
import { useNotificationStore } from "@/stores/NotificationStore";
import { useAudienceBuilderStore } from "@/stores/AudienceBuilderStore";
import { useTaskStore } from "@/stores/TaskStore";
import { findOptionForValue } from "@/helpers/incomeDisplayBands";
import {
  defaultDemographics,
  demographicsPayload,
  type Demographics,
} from "@/stores/helpers/researchDemographics";
type schemas = components["schemas"];

type Loading = {
  surveys: boolean;
  kpis: boolean;
  campaigns: boolean;
  savingCampaign: boolean;
  deletingCampaign: boolean;
  surveyQuestions: boolean;
  customDemographics: boolean;
  createCustomDemographic: boolean;
  deleteCustomDemographic: boolean;
  surveyDataSingle: boolean;
  surveyDataTrended: boolean;
  surveyDataTrendedQuestionId: undefined | number;
  modeledDataSingle: boolean;
  modeledDataTrended: boolean;
};

type Errors = {
  surveyDataSingle: string | undefined;
  surveyDataTrended: string | undefined;
  surveyDataTrendedQuestionId:
    { question_id: number; message: string } | undefined;
  modeledDataSingle: string | undefined;
  modeledDataTrended: string | undefined;
};

type TaskIds = {
  surveys: string | undefined;
  kpis: string | undefined;
  campaigns: string | undefined;
  surveyDataSingle: string | undefined;
  surveyDataTrended: string | undefined;
  surveyDataTrendedQuestionId: string | undefined;
  modeledDataSingle: string | undefined;
  modeledDataTrended: string | undefined;
};

type Geographies = {
  id: number;
  name: string;
};

type Audiences = {
  id: number;
  name: string;
};

type QuestionPayload = {
  question_id: number;
  kpi_id: number;
  positive_responses: number[];
  negative_responses: number[];
};

export const useResearchStore = defineStore("research", () => {
  const { $ws } = usePlugins();
  const errorStore = useErrorStore();
  const notificationStore = useNotificationStore();
  const audienceBuilderStore = useAudienceBuilderStore();
  const taskStore = useTaskStore();
  const surveys = ref<schemas["ResearchLibrarySurveyResponse"][]>([]);
  const kpis = ref<schemas["ResearchLibraryKpiResponse"][]>([]);
  const campaigns = ref<schemas["ResearchLibraryCampaignResponse"][]>([]);
  const surveyQuestions =
    ref<schemas["ResearchLibraryListSurveyQuestionsResponse"][]>();
  const customDemographics = ref<
    schemas["ResearchLibraryListCustomDemographicsResponse"][]
  >([]);

  const errors = ref<Errors>({
    surveyDataSingle: undefined,
    surveyDataTrended: undefined,
    surveyDataTrendedQuestionId: undefined,
    modeledDataSingle: undefined,
    modeledDataTrended: undefined,
  });
  const loading = ref<Loading>({
    surveys: false,
    kpis: false,
    campaigns: false,
    savingCampaign: false,
    deletingCampaign: false,
    surveyQuestions: false,
    customDemographics: false,
    createCustomDemographic: false,
    deleteCustomDemographic: false,
    surveyDataSingle: false,
    surveyDataTrended: false,
    surveyDataTrendedQuestionId: undefined,
    modeledDataSingle: false,
    modeledDataTrended: false,
  });
  const taskIds = ref<TaskIds>({
    surveys: undefined,
    kpis: undefined,
    campaigns: undefined,
    surveyDataSingle: undefined,
    surveyDataTrended: undefined,
    surveyDataTrendedQuestionId: undefined,
    modeledDataSingle: undefined,
    modeledDataTrended: undefined,
  });
  const cancelTask = async (key: keyof TaskIds) => {
    if (taskIds.value[key]) {
      await taskStore.cancelTask(taskIds.value[key] as string);
      taskIds.value[key] = undefined;
    }
  };

  const selectedSurvey = ref<schemas["ResearchLibrarySurveyResponse"] | null>(
    null,
  );
  const selectedView = ref<{
    tab: "survey_data" | "modeled_data" | "audience_segmentation";
    chartType: "single" | "trended" | undefined;
  }>({
    tab: "survey_data",
    chartType: "single",
  });
  const selectedQuestions = ref<
    schemas["ResearchLibraryListSurveyQuestionsResponse"][]
  >([]);
  const cloneDefaultDemographics = (): Demographics =>
    structuredClone(defaultDemographics);
  const selectedDemographics = ref<Demographics>(cloneDefaultDemographics());
  const selectedCustomDemos = ref<
    schemas["ResearchLibraryListCustomDemographicsResponse"][]
  >([]);
  const selectedGeographies = ref<Geographies[]>([]);
  const selectedAudiences = ref<Audiences[]>([]);
  const surveyDataSingle = ref<schemas["QuestionData1"][]>();
  const surveyDataTrended = ref<schemas["TrendedQuestionData1"][]>();
  const modeledDataSingle =
    ref<schemas["ResearchLibrarySingleSurveyModeledDataFavorability"][]>();
  const modeledDataTrended = ref<schemas["TrendedQuestionModeledData1"][]>();

  const unreadSurveyIds = computed(() => {
    return notificationStore.notifications
      .filter((n) => !n.read && n.meta_data?.survey_id != null)
      .map((n) => n.meta_data.survey_id);
  });

  const getDemographicName = (
    key: string,
    value: boolean | string | number,
  ): string | undefined => {
    if (key === "all_adults") {
      return value ? "All Adults" : undefined;
    }
    if (key === "children_in_household") {
      return value ? "Children in Household" : "No Children In Household";
    }
    const demographicType = audienceBuilderStore.demographicTypes.find(
      (el: any) => el.name === key,
    );
    if (!demographicType) {
      return undefined;
    }
    return (
      findOptionForValue(demographicType.options, value as number | string)
        ?.name ??
      demographicType.options?.find(
        (el: any) =>
          el.value !== undefined && parseInt(el.value) === Number(value),
      )?.name
    );
  };

  const demographicNames = computed(() => {
    const result: string[] = [];
    for (const [key, value] of Object.entries(selectedDemographics.value)) {
      if (key === "all_adults") {
        if (value) {
          result.push("All Adults");
        }
        continue;
      }
      if (key === "custom_age_range") {
        if (Array.isArray(value) && value.length > 1) {
          result.push(value.join("-"));
        } else if (Array.isArray(value) && value.length === 1) {
          result.push(`${value[0]}+`);
        }
        continue;
      }
      if (Array.isArray(value)) {
        if (!value.length) {
          continue;
        }
        const seenNames = new Set<string>();
        value.forEach((v: boolean | string | number) => {
          const name = getDemographicName(key, v);
          if (!name || seenNames.has(name)) {
            return;
          }
          seenNames.add(name);
          result.push(name);
        });
      }
    }
    selectedCustomDemos.value.forEach((customDemo) => {
      result.push(customDemo.name);
    });
    return result;
  });

  const surveyResponses = computed(() => {
    return surveyQuestions.value?.map((q) => ({
      kpi: q.kpi,
      kpi_id: q.kpi_id,
      id: q.id,
      positive:
        q.question_choices && q.positive_responses
          ? q.positive_responses.map(
              (p) =>
                (q.question_choices as Record<number, string>)[p as number],
            )
          : undefined,
      positive_label: q.positive_sentiment_label,
      negative:
        q.question_choices && q.negative_responses
          ? q.negative_responses.map(
              (n) =>
                (q.question_choices as Record<number, string>)[n as number],
            )
          : undefined,
      negative_label: q.negative_sentiment_label,
      question_choices: q.question_choices as Record<number, string>,
    }));
  });

  return {
    loading,
    errors,
    taskIds,
    surveys,
    kpis,
    campaigns,
    surveyQuestions,
    customDemographics,
    unreadSurveyIds,
    selectedSurvey,
    selectedView,
    selectedQuestions,
    selectedDemographics,
    selectedCustomDemos,
    selectedGeographies,
    selectedAudiences,
    surveyDataSingle,
    surveyDataTrended,
    modeledDataSingle,
    modeledDataTrended,
    demographicNames,
    surveyResponses,
    cancelTask,
    getChartRequestOrder() {
      const chartMethods = [
        "getSurveyDataSingle",
        "getSurveyDataTrended",
        "getModeledDataSingle",
        "getModeledDataTrended",
      ];
      const firstMethod =
        selectedView.value.tab === "audience_segmentation"
          ? "getSurveyDataSingle"
          : selectedView.value.tab === "survey_data"
            ? selectedView.value.chartType === "trended"
              ? "getSurveyDataTrended"
              : "getSurveyDataSingle"
            : selectedView.value.chartType === "trended"
              ? "getModeledDataTrended"
              : "getModeledDataSingle";
      const remainingMethods = chartMethods.filter(
        (method) => method !== firstMethod,
      );
      return { firstMethod, remainingMethods };
    },
    resetDemographics() {
      selectedDemographics.value = cloneDefaultDemographics();
      selectedCustomDemos.value = [];
    },
    async listSurveys(search?: string) {
      loading.value.surveys = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/research/library/surveys",
          {
            params: {
              query: {
                search: search ?? undefined,
              },
            },
          },
        );
        if (error || !data) {
          errorStore.showError(
            error?.message ?? errorStore.defaultErrorMessage,
          );
          loading.value.surveys = false;
          return;
        }
        taskIds.value.surveys = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error: any) {
        console.error(error);
        errorStore.showError(error?.message ?? errorStore.defaultErrorMessage);
        taskIds.value.surveys = undefined;
        loading.value.surveys = false;
      }
    },
    async listKpis(search?: string) {
      loading.value.kpis = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/research/library/kpis",
          {
            params: {
              query: {
                search: search ?? undefined,
              },
            },
          },
        );
        if (error || !data) {
          errorStore.showError(
            error?.message ?? errorStore.defaultErrorMessage,
          );
          loading.value.kpis = false;
          return;
        }
        taskIds.value.kpis = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error: any) {
        console.error(error);
        errorStore.showError(error?.message ?? errorStore.defaultErrorMessage);
        taskIds.value.kpis = undefined;
        loading.value.kpis = false;
      }
    },
    async listCampaigns(search?: string) {
      loading.value.campaigns = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/research/library/campaigns",
          {
            params: {
              query: {
                search: search ?? undefined,
              },
            },
          },
        );
        if (error || !data) {
          errorStore.showError(
            error?.message ?? errorStore.defaultErrorMessage,
          );
          loading.value.campaigns = false;
          return;
        }
        taskIds.value.campaigns = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error: any) {
        console.error(error);
        errorStore.showError(error?.message ?? errorStore.defaultErrorMessage);
        taskIds.value.campaigns = undefined;
        loading.value.campaigns = false;
      }
    },
    async createCampaign(
      payload: schemas["ResearchLibraryCreateCampaignRequest"],
    ) {
      loading.value.savingCampaign = true;
      try {
        const { data, error } = await client
          .POST("/api/v1/research/library/campaign", { body: payload })
          .catch((error: any) => {
            errorStore.showError(
              error?.message ?? errorStore.defaultErrorMessage,
            );
            throw error;
          });
        if (error) {
          errorStore.showError(
            error?.message ?? errorStore.defaultErrorMessage,
          );
          throw error;
        }
        return data;
      } finally {
        loading.value.savingCampaign = false;
      }
    },
    async updateCampaign(
      payload: schemas["ResearchLibraryUpdateCampaignRequest"],
    ) {
      loading.value.savingCampaign = true;
      try {
        const { data, error } = await client
          .PUT("/api/v1/research/library/campaign", { body: payload })
          .catch((error: any) => {
            errorStore.showError(
              error?.message ?? errorStore.defaultErrorMessage,
            );
            throw error;
          });
        if (error) {
          errorStore.showError(
            error?.message ?? errorStore.defaultErrorMessage,
          );
          throw error;
        }
        return data;
      } finally {
        loading.value.savingCampaign = false;
      }
    },
    async deleteCampaign(id: number) {
      loading.value.deletingCampaign = true;
      try {
        const { data, error } = await client
          .DELETE("/api/v1/research/library/campaign/{campaign_id}", {
            params: { path: { campaign_id: id } },
          })
          .catch((error: any) => {
            errorStore.showError(
              error?.message ?? errorStore.defaultErrorMessage,
            );
            throw error;
          });
        if (error) {
          errorStore.showError(
            error?.message ?? errorStore.defaultErrorMessage,
          );
          throw error;
        }
        return data;
      } finally {
        loading.value.deletingCampaign = false;
      }
    },
    async getSurveyQuestions(surveyId: number) {
      loading.value.surveyQuestions = true;
      surveyQuestions.value = [];
      try {
        const { data, error } = await client.GET(
          "/api/v1/research/library/survey_questions/{survey_id}",
          {
            params: {
              path: {
                survey_id: surveyId,
              },
            },
          },
        );
        if (error) {
          throw error;
        }
        surveyQuestions.value = data;
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.surveyQuestions = false;
      }
    },
    async getCustomDemographics() {
      loading.value.customDemographics = true;
      const { data, error } = await client.GET(
        "/api/v1/research/library/custom_demographics",
      );
      if (error) {
        loading.value.customDemographics = false;
        return;
      }
      customDemographics.value = data ?? [];
      loading.value.customDemographics = false;
    },
    async createCustomDemographic(
      payload: schemas["ResearchLibraryCreateCustomDemographicRequest"],
    ) {
      loading.value.createCustomDemographic = true;
      payload.demographics = demographicsPayload(
        payload.demographics as any,
        audienceBuilderStore,
      );
      const { data } = await client.POST(
        "/api/v1/research/library/custom_demographic",
        {
          body: payload,
        },
      );
      if (data) {
        customDemographics.value.push({
          id: data.id,
          name: data.name,
          demographics: data.demo_json,
        });
      }
      loading.value.createCustomDemographic = false;
    },
    async updateCustomDemographic(
      payload: schemas["ResearchLibraryUpdateCustomDemographicRequest"],
    ) {
      loading.value.createCustomDemographic = true;
      payload.demographics = demographicsPayload(
        payload.demographics as any,
        audienceBuilderStore,
      );
      const { data } = await client.PUT(
        "/api/v1/research/library/custom_demographic",
        {
          body: payload,
        },
      );
      if (data) {
        customDemographics.value = customDemographics.value.map((demo) => {
          if (demo.id === data.id) {
            return {
              ...demo,
              name: data.name,
              demographics: data.demo_json,
            };
          }
          return demo;
        });
      }
      loading.value.createCustomDemographic = false;
    },
    async deleteCustomDemographic(customDemographicId: number) {
      loading.value.deleteCustomDemographic = true;
      try {
        const { data } = await client.DELETE(
          "/api/v1/research/library/custom_demographic/{custom_demographic_id}",
          {
            params: {
              path: {
                custom_demographic_id: customDemographicId,
              },
            },
          },
        );
        if (data) {
          customDemographics.value = customDemographics.value.filter(
            (d) => d.id !== customDemographicId,
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.deleteCustomDemographic = false;
      }
    },
    async getSurveyDataSingle() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("surveyDataSingle");
      errors.value.surveyDataSingle = undefined;
      loading.value.surveyDataSingle = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchLibraryListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research/results/survey/single_survey_question_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: Number(q.question_id),
                kpi_id: Number(q.kpi_id),
              })),
              demographics: demographicsPayload(
                selectedDemographics.value,
                audienceBuilderStore,
              ),
              custom_demographics: customDemoIds,
            } as schemas["ResearchLibrarySingleSurveyQuestionDataRequest"],
          },
        );
        taskIds.value.surveyDataSingle = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.surveyDataSingle = undefined;
        loading.value.surveyDataSingle = false;
      }
    },
    async getSurveyDataTrended() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("surveyDataTrended");
      errors.value.surveyDataTrended = undefined;
      loading.value.surveyDataTrended = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchLibraryListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research/results/trended_question_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: Number(q.question_id),
                kpi_id: Number(q.kpi_id),
                positive_responses: q.positive_responses ?? null,
                negative_responses: q.negative_responses ?? null,
              })),
              demographics: demographicsPayload(
                selectedDemographics.value,
                audienceBuilderStore,
              ),
              custom_demographics: customDemoIds,
            },
          },
        );
        taskIds.value.surveyDataTrended = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.surveyDataTrended = undefined;
        loading.value.surveyDataTrended = false;
      }
    },
    async getSurveyDataTrendedQuestionId(question: QuestionPayload) {
      const surveyId = selectedSurvey.value?.id;

      if (!surveyId || !question) return;
      errors.value.surveyDataTrendedQuestionId = undefined;
      loading.value.surveyDataTrendedQuestionId = question.question_id;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchLibraryListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research/results/trended_question_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: [question],
              demographics: demographicsPayload(
                selectedDemographics.value,
                audienceBuilderStore,
              ),
              custom_demographics: customDemoIds,
            },
          },
        );
        taskIds.value.surveyDataTrendedQuestionId = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.surveyDataTrendedQuestionId = undefined;
        loading.value.surveyDataTrendedQuestionId = undefined;
      }
    },
    async getModeledDataSingle() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("modeledDataSingle");
      errors.value.modeledDataSingle = undefined;
      loading.value.modeledDataSingle = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchLibraryListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research/results/survey/single_survey_modeled_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: Number(q.question_id),
                kpi_id: Number(q.kpi_id),
                positive_responses: q.positive_responses ?? null,
                negative_responses: q.negative_responses ?? null,
              })),
              demographics: demographicsPayload(
                selectedDemographics.value,
                audienceBuilderStore,
              ),
              custom_demographics: customDemoIds,
              geographies: selectedGeographies.value,
              meta_audience_ids: selectedAudiences.value.map((a) => a.id),
            },
          },
        );
        taskIds.value.modeledDataSingle = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.modeledDataSingle = undefined;
        loading.value.modeledDataSingle = false;
      }
    },
    async getModeledDataTrended() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("modeledDataTrended");
      errors.value.modeledDataTrended = undefined;
      loading.value.modeledDataTrended = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchLibraryListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research/results/trended_survey_modeled_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: Number(q.question_id),
                kpi_id: Number(q.kpi_id),
                positive_responses: q.positive_responses ?? null,
                negative_responses: q.negative_responses ?? null,
              })),
              demographics: demographicsPayload(
                selectedDemographics.value,
                audienceBuilderStore,
              ),
              custom_demographics: customDemoIds,
              geographies: selectedGeographies.value,
              meta_audience_ids: selectedAudiences.value.map((a) => a.id),
            },
          },
        );
        taskIds.value.modeledDataTrended = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "Research",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.modeledDataTrended = undefined;
        loading.value.modeledDataTrended = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useResearchStore, import.meta.hot));
}
